/**
 * FormFlu Empty State Component
 * 
 * Displayed when there's no data to show (no forms, no submissions, etc.)
 * Uses the sick mascot to represent "things that need attention"
 */

import './EmptyState.css';

type EmptyStateType = 'forms' | 'submissions' | 'analytics' | 'tests' | 'generic';

interface EmptyStateProps {
  type?: EmptyStateType;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  showMascot?: boolean;
}

const defaultContent: Record<EmptyStateType, { title: string; description: string; actionLabel: string }> = {
  forms: {
    title: 'No forms yet',
    description: 'Your forms are feeling under the weather. Create your first form to start collecting responses and boost your conversion rates.',
    actionLabel: 'Create Your First Form',
  },
  submissions: {
    title: 'No submissions yet',
    description: 'Once patients start filling out your forms, their submissions will appear here. Share your form to get started.',
    actionLabel: 'View Your Forms',
  },
  analytics: {
    title: 'No analytics data yet',
    description: 'Analytics data will appear once your forms start receiving traffic. Create and publish a form to begin tracking.',
    actionLabel: 'Create a Form',
  },
  tests: {
    title: 'No A/B tests running',
    description: 'Set up your first A/B test to start optimizing your form conversions. Test different layouts, questions, and designs.',
    actionLabel: 'Create an A/B Test',
  },
  generic: {
    title: 'Nothing here yet',
    description: 'This section is empty. Get started by adding some content.',
    actionLabel: 'Get Started',
  },
};

export function EmptyState({
  type = 'generic',
  title,
  description,
  actionLabel,
  onAction,
  showMascot = true,
}: EmptyStateProps) {
  const content = defaultContent[type];

  return (
    <div className="empty-state">
      {showMascot && (
        <div className="empty-state-mascot">
          <img 
            src="/images/finalformflumainlogo.png" 
            alt="Sick form mascot - needs attention"
            className="empty-state-mascot-img"
          />
          <div className="empty-state-mascot-speech">
            <span>🤒 Help me get healthy!</span>
          </div>
        </div>
      )}
      
      <div className="empty-state-content">
        <h3 className="empty-state-title">{title || content.title}</h3>
        <p className="empty-state-description">{description || content.description}</p>
        
        {onAction && (
          <button onClick={onAction} className="btn btn-primary">
            {actionLabel || content.actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Success State Component
 * 
 * Used to show when something good happens (form published, high conversions, etc.)
 * Uses the healthy mascot to celebrate wins!
 */
interface SuccessStateProps {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}

export function SuccessState({
  title,
  description,
  actionLabel,
  onAction,
}: SuccessStateProps) {
  return (
    <div className="success-state">
      <div className="success-state-mascot">
        <img 
          src="/images/finalformfluhealthylogo.png" 
          alt="Healthy form mascot - success!"
          className="success-state-mascot-img"
        />
        <div className="success-state-confetti">
          <span>🎉</span>
          <span>✨</span>
          <span>💪</span>
        </div>
      </div>
      
      <div className="success-state-content">
        <h3 className="success-state-title">{title}</h3>
        <p className="success-state-description">{description}</p>
        
        {onAction && actionLabel && (
          <button onClick={onAction} className="btn btn-primary">
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}
